import React, {useMemo} from 'react'
import PropTypes from 'prop-types'
import {Box, useSlotRecipe} from '@chakra-ui/react3'
import {useIntl} from 'react-intl'
import {ChevronLeftIcon} from '../../../icons'
import RichText from '../../../cms/rich-text'
import {flyoutNavigationLevel3BreadcrumbRecipe} from './flyout-navigation-level3-breadcrumb.recipe'

/**
 * Shared breadcrumb for both level-3 navigation shapes.
 *
 * The breadcrumb shows on both viewports, unlike level 2's: level 3 replaces the
 * level-1 list as well as the level-2 pane, so desktop has nothing left on
 * screen to drill back out to. Desktop names the category it came from beside
 * the chevron; mobile shows only the level the user is on. The trailing crumb
 * is always italic (STOK-17026 §3).
 *
 * @param {Object} props
 * @param {string} [props.title] - Current level title.
 * @param {string} [props.parentTitle] - Parent category title.
 * @param {Function} props.onBack - Returns to the level-2 pane.
 * @returns {JSX.Element}
 */
const FlyoutNavigationLevel3Breadcrumb = ({title, parentTitle, onBack}) => {
    const recipe = useSlotRecipe({recipe: flyoutNavigationLevel3BreadcrumbRecipe})
    const styles = useMemo(() => recipe(), [recipe])
    const {formatMessage} = useIntl()

    return (
        <Box css={styles.breadcrumb}>
            <Box
                as="button"
                type="button"
                css={styles.back}
                onClick={onBack}
                aria-label={formatMessage(
                    {
                        id: 'leap.navigation.level3.back',
                        defaultMessage: 'Back to {title}'
                    },
                    {title: parentTitle}
                )}
            >
                <Box css={styles.backIcon} aria-hidden="true">
                    <ChevronLeftIcon />
                </Box>
                <RichText as="span" css={styles.backLabel} aria-hidden="true">
                    {parentTitle}
                </RichText>
            </Box>
            {parentTitle && (
                <Box as="span" css={styles.breadcrumbDivider} aria-hidden="true">
                    /
                </Box>
            )}
            <RichText as="span" css={styles.breadcrumbCurrent}>
                {title}
            </RichText>
        </Box>
    )
}

FlyoutNavigationLevel3Breadcrumb.propTypes = {
    title: PropTypes.string,
    parentTitle: PropTypes.string,
    onBack: PropTypes.func.isRequired
}

export default FlyoutNavigationLevel3Breadcrumb
